import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';

const COUNTRY_OPTIONS = [
  'Palestine',
  'Jordan',
  'Lebanon',
  'Syria',
  'Egypt',
  'Iraq',
  'Saudi Arabia',
  'United Arab Emirates',
  'Kuwait',
  'Qatar',
  'Bahrain',
  'Oman',
  'Yemen',
  'Morocco',
  'Algeria',
  'Tunisia',
  'Libya',
  'Sudan',
  'Iran',
  'Other',
];

const FESTIVAL_ATTRACTION_OPTIONS = [
  'FIFA Watch Party',
  'Food',
  'Festival Stage Performers',
  'Hookah Lounge',
  'Indoor Shopping Bazaar',
  'Multi Faith Space',
  'Panel Discussions',
  'Little Palestine',
  'Art Exhibits',
  'The Rides',
  'Festival Vibes',
];

const SURVEY_CONFIG = {
  vendor: {
    title: 'Vendor Survey',
    intro: 'Thank you for vending with us. Your feedback helps us improve next year.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall experience at the festival?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'vend_again', type: 'radio', label: 'How likely are you to vend with us again next year?', options: ['Definitely', 'Probably', 'Not Sure', 'Probably Not', 'Definitely Not'] },
      {
        id: 'recommend_vendor',
        type: 'scale',
        label: 'How likely are you to recommend this festival to another vendor? (0–10)',
        helperText: '0 = Not Likely At All, 10 = Extremely Likely',
        scaleLeftLabel: 'Not Likely At All',
        scaleRightLabel: 'Extremely Likely',
      },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      { id: 'sales_performance', type: 'radio', label: 'How would you rate your sales performance?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'foot_traffic', type: 'radio', label: 'How was the foot traffic near your booth?', options: ['Very Strong', 'Good', 'Average', 'Low', 'Very Low'] },
      { id: 'strongest_day', type: 'radio', label: 'Which day was your strongest sales day?', options: ['Friday', 'Saturday', 'Sunday', 'All Were Similar'] },
      { id: 'increase_sales_help', type: 'textarea', label: 'What would have helped increase your sales?' },
      { id: 'checkin_setup_process', type: 'radio', label: 'How was the vendor check-in and setup process?', options: ['Very Smooth', 'Smooth', 'Average', 'Difficult', 'Very Difficult'] },
      { id: 'setup_instructions', type: 'radio', label: 'Were setup instructions clear before the event?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'load_in_out', type: 'radio', label: 'Was load-in/load-out organized?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'resources_expected', type: 'radio', label: 'Did you have the space, power, water, or other resources you expected?', options: ['Yes', 'Somewhat', 'No', 'Not Applicable'] },
      { id: 'staff_responsive', type: 'radio', label: 'Were festival staff responsive when you needed help?', options: ['Yes', 'Somewhat', 'No', 'I Did Not Need Help'] },
      { id: 'next_year_option', type: 'radio', label: 'What booth size or vendor option would you be interested in next year?', options: ['Same As This Year', 'Larger Booth', 'Smaller Booth', 'Food Truck Space', 'Premium Location', 'Sponsorship Package', 'Not Sure'] },
      {
        id: 'support_next_year',
        type: 'checkbox',
        label: 'What types of vendor support would be most helpful next year? (Select all that apply)',
        options: [
          'Better Load-In Instructions',
          'More Signage',
          'More Marketing Before Event',
          'Vendor Map Promotion',
          'Dedicated Vendor Support Contact',
          'More Seating Near Vendor Areas',
          'More Trash Support',
          'Other',
        ],
        allowOther: true,
      },
      { id: 'keep_same', type: 'textarea', label: 'What should we keep the same for vendors next year?' },
      { id: 'improve_next_year', type: 'textarea', label: 'What should we improve for vendors next year?' },
      { id: 'additional_comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
  attendee_sat: {
    title: 'Attendee Survey (Sat PAC AMP)',
    intro: 'Thanks for coming to the festival. We appreciate your feedback.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall experience at the festival?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'attend_again', type: 'radio', label: 'How likely are you to attend again next year?', options: ['Definitely', 'Probably', 'Not Sure', 'Probably Not', 'Definitely Not'] },
      {
        id: 'recommend_festival',
        type: 'scale',
        label: 'How likely are you to recommend the festival to friends or family?',
        helperText: '0 = Not Likely At All, 10 = Extremely Likely',
        scaleLeftLabel: 'Not Likely At All',
        scaleRightLabel: 'Extremely Likely',
      },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      {
        id: 'heard_about',
        type: 'checkbox',
        label: 'Where did you hear about the festival? (Select all that apply)',
        options: ['Social media', 'Friend or family', 'Flyer', 'Billboard', 'Artist Post'],
        allowOther: true,
      },
      {
        id: 'pac_amp_saturday_compelled',
        type: 'select',
        label: 'Which PAC AMP Saturday (6/20) performer compelled you to book your ticket?',
        options: ['Dystinct', 'Tul8te', 'Bayou'],
      },
      {
        id: 'did_attend_festival_in_addition',
        type: 'radio',
        label: 'Did you also attend the festival?',
        options: ['Yes', 'No'],
      },
      {
        id: 'festival_plus_concert_attraction',
        type: 'select',
        label: 'What attracted you to come to the festival in addition to the concert?',
        options: FESTIVAL_ATTRACTION_OPTIONS,
        showWhen: { id: 'did_attend_festival_in_addition', equals: 'Yes' },
      },
      {
        id: 'areas_visited',
        type: 'checkbox',
        label: 'Which areas did you visit? (Select all that apply)',
        options: ['Art Exhibits', 'Bazaar', 'Carnival Rides', 'Festival Stage Performances', 'FIFA Watch Party', 'Food Court', 'Hookah Lounge', 'Indoor Shopping Bazaar', 'Kids Activities', 'Little Palestine Activation', 'Multi-Faith Space', 'Pacific Amphitheatre Stage Performers', 'Panel Discussions', 'VIP Lounge'],
      },
      { id: 'entry_checkin', type: 'radio', label: 'How was your entry/check-in experience?', options: ['Very Smooth', 'Smooth', 'Average', 'Slow', 'Very Difficult'] },
      { id: 'wait_time', type: 'radio', label: 'How long did you wait to enter the festival?', options: ['Less Than 5 Minutes', '5–15 Minutes', '15–30 Minutes', '30–60 Minutes', 'Over 1 Hour'] },
      { id: 'food_options', type: 'radio', label: 'How would you rate the food options?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Purchase Food'] },
      { id: 'bazaar_experience', type: 'radio', label: 'How would you rate the bazaar/vendor shopping experience?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Visit'] },
      { id: 'family_kids_activities', type: 'radio', label: 'Were there enough activities for families and kids?', options: ['Yes', 'Somewhat', 'No', 'Not Applicable'] },
      { id: 'seating_shade', type: 'radio', label: 'Was there enough seating and shaded/rest areas at the festival?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'panel_discussions', type: 'radio', label: 'Would you like to see more panel discussions at the indoor stage?', options: ['Yes', 'No'] },
      {
        id: 'pac_amp_next_year_artist',
        type: 'textarea',
        label: 'Which artist or performer would you like to see perform next year?',
      },
      { id: 'keep_same', type: 'textarea', label: 'What is one thing we should keep exactly the same next year?' },
      { id: 'change_improve', type: 'textarea', label: 'What is one thing we should change or improve next year?' },
      { id: 'additional_comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
  attendee_sun: {
    title: 'Attendee Survey (Sun PAC AMP)',
    intro: 'Thanks for coming to the festival. We appreciate your feedback.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall experience at the festival?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'attend_again', type: 'radio', label: 'How likely are you to attend again next year?', options: ['Definitely', 'Probably', 'Not Sure', 'Probably Not', 'Definitely Not'] },
      {
        id: 'recommend_festival',
        type: 'scale',
        label: 'How likely are you to recommend the festival to friends or family?',
        helperText: '0 = Not Likely At All, 10 = Extremely Likely',
        scaleLeftLabel: 'Not Likely At All',
        scaleRightLabel: 'Extremely Likely',
      },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      {
        id: 'heard_about',
        type: 'checkbox',
        label: 'Where did you hear about the festival? (Select all that apply)',
        options: ['Social media', 'Friend or family', 'Flyer', 'Billboard', 'Artist Post'],
        allowOther: true,
      },
      {
        id: 'pac_amp_sunday_compelled',
        type: 'select',
        label: 'Which PAC AMP Sunday (6/21) performer compelled you to book your ticket?',
        options: ['Mohammed Assaf', 'Dana Salah', 'Issam Alnajjar'],
      },
      {
        id: 'did_attend_festival_in_addition',
        type: 'radio',
        label: 'Did you also attend the festival?',
        options: ['Yes', 'No'],
      },
      {
        id: 'festival_plus_concert_attraction',
        type: 'select',
        label: 'What attracted you to come to the festival in addition to the concert?',
        options: FESTIVAL_ATTRACTION_OPTIONS,
        showWhen: { id: 'did_attend_festival_in_addition', equals: 'Yes' },
      },
      {
        id: 'areas_visited',
        type: 'checkbox',
        label: 'Which areas did you visit? (Select all that apply)',
        options: ['Art Exhibits', 'Bazaar', 'Carnival Rides', 'Festival Stage Performances', 'FIFA Watch Party', 'Food Court', 'Hookah Lounge', 'Indoor Shopping Bazaar', 'Kids Activities', 'Little Palestine Activation', 'Multi-Faith Space', 'Pacific Amphitheatre Stage Performers', 'Panel Discussions', 'VIP Lounge'],
      },
      { id: 'entry_checkin', type: 'radio', label: 'How was your entry/check-in experience?', options: ['Very Smooth', 'Smooth', 'Average', 'Slow', 'Very Difficult'] },
      { id: 'wait_time', type: 'radio', label: 'How long did you wait to enter the festival?', options: ['Less Than 5 Minutes', '5–15 Minutes', '15–30 Minutes', '30–60 Minutes', 'Over 1 Hour'] },
      { id: 'food_options', type: 'radio', label: 'How would you rate the food options?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Purchase Food'] },
      { id: 'bazaar_experience', type: 'radio', label: 'How would you rate the bazaar/vendor shopping experience?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Visit'] },
      { id: 'family_kids_activities', type: 'radio', label: 'Were there enough activities for families and kids?', options: ['Yes', 'Somewhat', 'No', 'Not Applicable'] },
      { id: 'seating_shade', type: 'radio', label: 'Was there enough seating and shaded/rest areas at the festival?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'panel_discussions', type: 'radio', label: 'Would you like to see more panel discussions at the indoor stage?', options: ['Yes', 'No'] },
      {
        id: 'pac_amp_next_year_artist',
        type: 'textarea',
        label: 'Which artist or performer would you like to see perform next year?',
      },
      { id: 'keep_same', type: 'textarea', label: 'What is one thing we should keep exactly the same next year?' },
      { id: 'change_improve', type: 'textarea', label: 'What is one thing we should change or improve next year?' },
      { id: 'additional_comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
  attendee_both: {
    title: 'Attendee Survey (Sat+Sun PAC AMP)',
    intro: 'Thanks for coming to the festival. We appreciate your feedback.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall experience at the festival?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'attend_again', type: 'radio', label: 'How likely are you to attend again next year?', options: ['Definitely', 'Probably', 'Not Sure', 'Probably Not', 'Definitely Not'] },
      {
        id: 'recommend_festival',
        type: 'scale',
        label: 'How likely are you to recommend the festival to friends or family?',
        helperText: '0 = Not Likely At All, 10 = Extremely Likely',
        scaleLeftLabel: 'Not Likely At All',
        scaleRightLabel: 'Extremely Likely',
      },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      {
        id: 'heard_about',
        type: 'checkbox',
        label: 'Where did you hear about the festival? (Select all that apply)',
        options: ['Social media', 'Friend or family', 'Flyer', 'Billboard', 'Artist Post'],
        allowOther: true,
      },
      {
        id: 'pac_amp_saturday_compelled',
        type: 'select',
        label: 'Which PAC AMP Saturday (6/20) performer compelled you to book your ticket?',
        options: ['Dystinct', 'Tul8te', 'Bayou'],
      },
      {
        id: 'pac_amp_sunday_compelled',
        type: 'select',
        label: 'Which PAC AMP Sunday (6/21) performer compelled you to book your ticket?',
        options: ['Mohammed Assaf', 'Dana Salah', 'Issam Alnajjar'],
      },
      {
        id: 'did_attend_festival_in_addition',
        type: 'radio',
        label: 'Did you also attend the festival?',
        options: ['Yes', 'No'],
      },
      {
        id: 'festival_plus_concert_attraction',
        type: 'select',
        label: 'What attracted you to come to the festival in addition to the concert?',
        options: FESTIVAL_ATTRACTION_OPTIONS,
        showWhen: { id: 'did_attend_festival_in_addition', equals: 'Yes' },
      },
      {
        id: 'areas_visited',
        type: 'checkbox',
        label: 'Which areas did you visit? (Select all that apply)',
        options: ['Art Exhibits', 'Bazaar', 'Carnival Rides', 'Festival Stage Performances', 'FIFA Watch Party', 'Food Court', 'Hookah Lounge', 'Indoor Shopping Bazaar', 'Kids Activities', 'Little Palestine Activation', 'Multi-Faith Space', 'Pacific Amphitheatre Stage Performers', 'Panel Discussions', 'VIP Lounge'],
      },
      { id: 'entry_checkin', type: 'radio', label: 'How was your entry/check-in experience?', options: ['Very Smooth', 'Smooth', 'Average', 'Slow', 'Very Difficult'] },
      { id: 'wait_time', type: 'radio', label: 'How long did you wait to enter the festival?', options: ['Less Than 5 Minutes', '5–15 Minutes', '15–30 Minutes', '30–60 Minutes', 'Over 1 Hour'] },
      { id: 'food_options', type: 'radio', label: 'How would you rate the food options?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Purchase Food'] },
      { id: 'bazaar_experience', type: 'radio', label: 'How would you rate the bazaar/vendor shopping experience?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Visit'] },
      { id: 'family_kids_activities', type: 'radio', label: 'Were there enough activities for families and kids?', options: ['Yes', 'Somewhat', 'No', 'Not Applicable'] },
      { id: 'seating_shade', type: 'radio', label: 'Was there enough seating and shaded/rest areas at the festival?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'panel_discussions', type: 'radio', label: 'Would you like to see more panel discussions at the indoor stage?', options: ['Yes', 'No'] },
      {
        id: 'pac_amp_next_year_artist',
        type: 'textarea',
        label: 'Which artist or performer would you like to see perform next year?',
      },
      { id: 'keep_same', type: 'textarea', label: 'What is one thing we should keep exactly the same next year?' },
      { id: 'change_improve', type: 'textarea', label: 'What is one thing we should change or improve next year?' },
      { id: 'additional_comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
  attendee_festival: {
    title: 'Attendee Survey (Festival Passes)',
    intro: 'Thanks for coming to the festival. We appreciate your feedback.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall experience at the festival?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'attend_again', type: 'radio', label: 'How likely are you to attend again next year?', options: ['Definitely', 'Probably', 'Not Sure', 'Probably Not', 'Definitely Not'] },
      {
        id: 'recommend_festival',
        type: 'scale',
        label: 'How likely are you to recommend the festival to friends or family?',
        helperText: '0 = Not Likely At All, 10 = Extremely Likely',
        scaleLeftLabel: 'Not Likely At All',
        scaleRightLabel: 'Extremely Likely',
      },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      {
        id: 'heard_about',
        type: 'checkbox',
        label: 'Where did you hear about the festival? (Select all that apply)',
        options: ['Social media', 'Friend or family', 'Flyer', 'Billboard', 'Artist Post'],
        allowOther: true,
      },
      {
        id: 'areas_visited',
        type: 'checkbox',
        label: 'Which areas did you visit? (Select all that apply)',
        options: ['Art Exhibits', 'Bazaar', 'Carnival Rides', 'Festival Stage Performances', 'FIFA Watch Party', 'Food Court', 'Hookah Lounge', 'Indoor Shopping Bazaar', 'Kids Activities', 'Little Palestine Activation', 'Multi-Faith Space', 'Pacific Amphitheatre Stage Performers', 'Panel Discussions', 'VIP Lounge'],
      },
      {
        id: 'festival_compelling_performer',
        type: 'checkbox',
        label: 'Which performer/speaker (if any) compelled you to book your ticket? (Select all that apply)',
        options: [
          '(Saturday) Al-Asala Dabke',
          '(Sunday) Al-Asala Dabke Dance',
          '(Sunday) Ana Kasparian',
          '(Friday) Anees',
          '(Saturday) Bayou',
          '(Saturday) CAIR Panel',
          '(Sunday) CAIR Panel',
          '(Sunday) DJ Ahmad',
          '(Friday) DJ Ahmed Black',
          '(Sunday) DJ Djamil',
          '(Friday) DJ Frayeh',
          '(Friday) DJ JuJu',
          '(Saturday) DJ L\'Rhythm',
          '(Saturday) DJ Nara',
          '(Saturday) DJ Nour',
          '(Friday) Ehab Tawfik',
          '(Saturday) Gaidaa',
          '(Sunday) Hamzah Saadah',
          '(Friday) Hussam Ayloush Khutbah',
          '(Sunday) Keemokazi & Family',
          '(Friday) Lana Lubany',
          '(Sunday) Mawtini Choir',
          '(Saturday) MC Abdul',
          '(Friday) Nasser Al-Rayess',
          '(Sunday) Norhan',
          '(Saturday) Oud Performance',
          '(Sunday) Palestinian Youth Ensemble',
          '(Friday) Saad Alessa'
        ],
        allowOther: true,
      },
      { id: 'entry_checkin', type: 'radio', label: 'How was your entry/check-in experience?', options: ['Very Smooth', 'Smooth', 'Average', 'Slow', 'Very Difficult'] },
      { id: 'wait_time', type: 'radio', label: 'How long did you wait to enter the festival?', options: ['Less Than 5 Minutes', '5–15 Minutes', '15–30 Minutes', '30–60 Minutes', 'Over 1 Hour'] },
      { id: 'food_options', type: 'radio', label: 'How would you rate the food options?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Purchase Food'] },
      { id: 'bazaar_experience', type: 'radio', label: 'How would you rate the bazaar/vendor shopping experience?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Did Not Visit'] },
      { id: 'family_kids_activities', type: 'radio', label: 'Were there enough activities for families and kids?', options: ['Yes', 'Somewhat', 'No', 'Not Applicable'] },
      { id: 'seating_shade', type: 'radio', label: 'Was there enough seating and shaded/rest areas at the festival?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'panel_discussions', type: 'radio', label: 'Would you like to see more panel discussions at the indoor stage?', options: ['Yes', 'No'] },
      { id: 'keep_same', type: 'textarea', label: 'What is one thing we should keep exactly the same next year?' },
      { id: 'change_improve', type: 'textarea', label: 'What is one thing we should change or improve next year?' },
      { id: 'additional_comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
  media: {
    title: 'Media Survey',
    intro: 'Thank you for covering the festival. Your feedback helps us improve the media experience.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall media/influencer experience?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'promote_again', type: 'radio', label: 'How likely are you to cover or promote the festival again in the future?', options: ['Definitely', 'Probably', 'Not Sure', 'Probably Not', 'Definitely Not'] },
      { id: 'content_opportunities', type: 'radio', label: 'Did the festival provide strong content opportunities?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      { id: 'checkin_experience', type: 'radio', label: 'How was the media check-in and credential pickup process?', options: ['Very Smooth', 'Smooth', 'Average', 'Slow', 'Difficult'] },
      { id: 'access_explained', type: 'radio', label: 'Were your access areas clearly explained?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'access_valuable', type: 'radio', label: 'Did you feel your media/influencer access was valuable?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'access_expected_but_could_not', type: 'radio', label: 'Were there any areas you expected to access but could not?', options: ['Yes', 'No'] },
      { id: 'access_expected_but_could_not_explain', type: 'textarea', label: 'If yes, please explain.', showWhen: { id: 'access_expected_but_could_not', equals: 'Yes' } },
      { id: 'capture_areas', type: 'checkbox', label: 'Which areas did you capture content from? Select all that apply.', options: ['Pacific Amphitheatre', 'Festival Grounds', 'Food Court', 'Bazaar', 'Carnival Rides', 'VIP Lounge', 'FIFA Watch Party', 'Backstage / Exclusive Areas', 'Artist Interviews', 'Crowd / Atmosphere', 'Hookah Lounge'] },
      { id: 'content_types', type: 'checkbox', label: 'What type of content did you create? Select all that apply.', options: ['Instagram Stories', 'Instagram Reels', 'TikTok Videos', 'YouTube Content', 'Blog / Article', 'Photography', 'Interviews', 'Livestream', 'Other'], allowOther: true },
      { id: 'helpful_info_earlier', type: 'checkbox', label: 'What information would have been helpful to receive earlier?', options: ['Parking / Arrival Instructions', 'Credential Pickup Info', 'Access Rules', 'Artist Schedule', 'Content Guidelines', 'Brand Tags / Hashtags', 'Contact Person', 'Map', 'Other'], allowOther: true },
      { id: 'better_content_next_year', type: 'checkbox', label: 'What would help you create better content next year?', options: ['Earlier Access', 'Better Photo Spots', 'Dedicated Media Area', 'Artist Interview Opportunities', 'Better Lighting', 'Clearer Schedule', 'Media Lounge', 'More Behind-The-Scenes Access', 'Other'], allowOther: true },
      { id: 'receive_assets_before', type: 'radio', label: 'Would you be interested in receiving official festival assets before the event? Examples: logos, graphics, artist images, captions, hashtags.', options: ['Yes', 'No'] },
      { id: 'collab_before_next', type: 'radio', label: 'Would you be interested in collaborating with us before the next festival?', options: ['Yes', 'Maybe', 'No'] },
      { id: 'collab_interests', type: 'checkbox', label: 'What kind of collaboration would interest you?', options: ['Ticket Giveaway', 'Sponsored Post', 'Behind-The-Scenes Content', 'Artist Interview', 'Festival Preview Video', 'Food/Vendor Feature', 'Other'], allowOther: true },
      { id: 'favorite_content_moment', type: 'textarea', label: 'What was your favorite content moment or area to capture?' },
      { id: 'improve_next_year', type: 'textarea', label: 'What could we improve for media and influencers next year?' },
      { id: 'links_to_content', type: 'textarea', label: 'Please share links to any content you posted from the festival' },
      { id: 'comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
  vip: {
    title: 'VIP Survey',
    intro: 'Thank you for attending as a VIP. Your feedback helps us elevate the experience.',
    questions: [
      { id: 'overall_experience', type: 'radio', label: 'How would you rate your overall VIP experience?', options: ['Excellent', 'Good', 'Average', 'Poor', 'Very Poor'] },
      { id: 'lounge_exclusive', type: 'radio', label: 'Did the VIP Lounge feel exclusive and elevated?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'enough_seating', type: 'radio', label: 'Was there enough seating available?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'country_identity', type: 'select', label: 'What country do you strongly identify with?', options: COUNTRY_OPTIONS },
      { id: 'country_identity_other', type: 'text', label: 'Please specify your country', showWhen: { id: 'country_identity', equals: 'Other' } },
      { id: 'food_quality', type: 'radio', label: 'How would you rate the quality of the food?', options: ['Excellent', 'Good', 'Average', 'Poor'] },
      { id: 'food_options', type: 'radio', label: 'Were there enough food options for different preferences or dietary needs?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'checkin_experience', type: 'radio', label: 'How was the VIP check-in experience?', options: ['Very Smooth', 'Smooth', 'Average', 'Slow', 'Difficult'] },
      { id: 'add_food_items', type: 'textarea', label: 'What food or beverage items would you like to see added next year?' },
      { id: 'access_explained', type: 'radio', label: 'Did your VIP access feel clearly explained before and during the event?', options: ['Yes', 'Somewhat', 'No'] },
      { id: 'sell_vip_next_year', type: 'radio', label: 'The VIP package was invite-only. Do you think we should sell VIP access tickets next year?', options: ['Yes', 'No'] },
      { id: 'favorite_part_textarea', type: 'textarea', label: 'What was your favorite part of the VIP experience?' },
      { id: 'improve_next_year', type: 'textarea', label: 'What should we change or improve for VIP guests next year?' },
      { id: 'comments', type: 'textarea', label: 'Any additional comments or suggestions?' },
    ],
  },
};

const shellStyle = {
  minHeight: '100vh',
  padding: '2rem 1rem',
  background: 'linear-gradient(135deg, #fff7f5 0%, #fff 35%, #fff8ed 100%)',
};

const cardStyle = {
  maxWidth: 880,
  margin: '0 auto',
  background: '#fff',
  border: '1px solid #f3e8e2',
  borderRadius: 18,
  padding: '1.25rem 1.25rem 1.5rem',
  boxShadow: '0 12px 28px rgba(0,0,0,0.06)',
};

const inputStyle = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid #e5e7eb',
  borderRadius: 10,
  padding: '0.6rem 0.75rem',
  fontSize: '0.92rem',
};

const PostFestivalSurveyPage = ({ surveyType, backendSurveyType }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const inviteId = searchParams.get('invite');
  const showSuccessState = searchParams.get('submitted') === '1';

  const config = useMemo(() => SURVEY_CONFIG[surveyType], [surveyType]);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [loadingInvite, setLoadingInvite] = useState(true);
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedAt, setSubmittedAt] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [answers, setAnswers] = useState({});
  const [isMobileLayout, setIsMobileLayout] = useState(() => (typeof window !== 'undefined' ? window.innerWidth <= 640 : false));

  useEffect(() => {
    const onResize = () => setIsMobileLayout(window.innerWidth <= 640);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const loadInvite = async () => {
      if (!inviteId) {
        setError('Missing invite ID in the survey link.');
        setLoadingInvite(false);
        return;
      }
      setLoadingInvite(true);
      try {
        const res = await api.getPostFestivalSurveyInvite(inviteId);
        if (!res?.success) {
          setError(res?.error?.message || 'Invalid survey link.');
          setLoadingInvite(false);
          return;
        }
        const data = res.data || {};
        const isFsh = data.email === 'fshmnoelhaki2@gmail.com';
        const expectedTypes = [surveyType, backendSurveyType].filter(Boolean);
        if (!isFsh && !expectedTypes.includes(data.survey_type)) {
          setError('This link belongs to a different survey type.');
          setLoadingInvite(false);
          return;
        }
        setInviteInfo(data);
        setFullName(data.full_name || '');
        setPhone(data.phone || '');
        setSubmittedAt(data.submitted_at || '');
      } catch (e) {
        setError(e.message || 'Failed to load survey invite.');
      } finally {
        setLoadingInvite(false);
      }
    };

    loadInvite();
  }, [inviteId, surveyType, backendSurveyType]);

  const shouldRenderQuestion = (question) => {
    const rule = question?.showWhen;
    if (!rule || !rule.id) return true;
    const current = answers[rule.id];
    if (Array.isArray(rule.in)) return rule.in.includes(current);
    if (Object.prototype.hasOwnProperty.call(rule, 'equals')) return current === rule.equals;
    if (Object.prototype.hasOwnProperty.call(rule, 'notEquals')) return current !== rule.notEquals;
    return true;
  };

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const toggleCheckbox = (id, option) => {
    setAnswers((prev) => {
      const current = Array.isArray(prev[id]) ? prev[id] : [];
      const next = current.includes(option) ? current.filter((v) => v !== option) : [...current, option];
      return { ...prev, [id]: next };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inviteId) return;

    setSubmitError('');
    setSubmitting(true);
    try {
      const payload = {
        full_name: fullName,
        phone,
        answers,
      };
      const res = await api.submitPostFestivalSurvey(inviteId, payload);
      if (!res?.success) {
        setSubmitError(res?.error?.message || 'Failed to submit survey.');
        return;
      }
      const nextSubmittedAt = res?.data?.submitted_at || new Date().toISOString();
      setSubmittedAt(nextSubmittedAt);

      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('invite', inviteId);
      nextParams.set('submitted', '1');
      setSearchParams(nextParams, { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSubmitError(err.message || 'Failed to submit survey.');
    } finally {
      setSubmitting(false);
    }
  };

  const backToForm = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('submitted');
    setSearchParams(nextParams, { replace: true });
  };

  return (
    <div style={shellStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 8, marginBottom: '1rem' }}>
          <img src="/logo.png" alt="OC MENA Festival" style={{ width: 70, height: 'auto' }} />
          <h1 style={{ margin: 0, fontSize: '1.4rem', color: '#111827' }}>{config.title}</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem', textAlign: 'center' }}>{config.intro}</p>
        </div>

        {loadingInvite && <div style={{ color: '#6b7280' }}>Loading survey link…</div>}
        {!loadingInvite && error && <div className="error-message" role="alert">{error}</div>}

        {!loadingInvite && !error && showSuccessState && (
          <div style={{ border: '1px solid #bbf7d0', background: '#ecfdf3', borderRadius: 14, padding: '1rem' }}>
            <h2 style={{ margin: 0, color: '#166534', fontSize: '1.15rem' }}>Thank you — your survey was submitted.</h2>
            <p style={{ margin: '8px 0 0', color: '#166534', fontSize: '0.9rem' }}>
              We received your response{submittedAt ? ` at ${new Date(submittedAt).toLocaleString()}` : ''}.
            </p>
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={backToForm}
                style={{ border: '1px solid #16a34a', borderRadius: 10, background: '#fff', color: '#166534', padding: '0.55rem 0.8rem', fontWeight: 700, cursor: 'pointer' }}
              >
                Back to form
              </button>
            </div>
          </div>
        )}

        {!loadingInvite && !error && !showSuccessState && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {!!submittedAt && (
              <div style={{ background: '#ecfdf3', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 10, padding: '0.6rem 0.8rem', fontSize: '0.88rem' }}>
                Your survey has already been submitted. You can submit again to update your answers.
              </div>
            )}

            <div style={{ background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.75rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#374151', fontSize: '0.86rem' }}>
                  Full name
                  <input value={fullName} disabled style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }} />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#374151', fontSize: '0.86rem' }}>
                  Phone
                  <input value={phone} disabled style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }} />
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, color: '#374151', fontSize: '0.86rem', marginTop: 8 }}>
                Email
                <input value={inviteInfo?.email || ''} disabled style={{ ...inputStyle, background: '#f9fafb', color: '#6b7280', cursor: 'not-allowed' }} />
              </label>
            </div>

            {config.questions.filter(shouldRenderQuestion).map((q, idx) => (
              <div key={q.id} style={{ border: '1px solid #eceff3', borderRadius: 12, padding: '0.9rem' }}>
                <div style={{ fontWeight: 700, color: '#111827', marginBottom: 10, fontSize: '0.92rem' }}>{`${idx + 1}. ${q.label}`}</div>
                {!!q.helperText && (
                  <div style={{ marginTop: -4, marginBottom: 10, color: '#64748b', fontSize: '0.8rem' }}>{q.helperText}</div>
                )}

                {q.type === 'radio' && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {q.options.map((opt) => (
                      <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#374151', fontSize: '0.9rem' }}>
                        <input
                          type="radio"
                          name={q.id}
                          checked={answers[q.id] === opt}
                          onChange={() => setAnswer(q.id, opt)}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                )}

                {q.type === 'scale' && (
                  <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '0.75rem', background: '#fafafa' }}>
                    {isMobileLayout ? (
                      <>
                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginBottom: 10 }}>
                          {q.scaleLeftLabel || '0'}
                        </div>
                        <div style={{ display: 'grid', gap: 8 }}>
                          {Array.from({ length: 11 }).map((_, n) => (
                            <label key={n} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, color: '#334155', fontSize: '0.86rem', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: '0.5rem 0.65rem' }}>
                              <span style={{ fontWeight: 700 }}>{n}</span>
                              <input
                                type="radio"
                                name={q.id}
                                checked={answers[q.id] === String(n)}
                                onChange={() => setAnswer(q.id, String(n))}
                              />
                            </label>
                          ))}
                        </div>
                        <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 10, textAlign: 'right' }}>
                          {q.scaleRightLabel || '10'}
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: '0.76rem', color: '#64748b', marginBottom: 10 }}>
                          <span>{q.scaleLeftLabel || '0'}</span>
                          <span>{q.scaleRightLabel || '10'}</span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(11, minmax(34px, 1fr))', gap: 6 }}>
                          {Array.from({ length: 11 }).map((_, n) => (
                            <label key={n} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#334155', fontSize: '0.78rem' }}>
                              <span>{n}</span>
                              <input
                                type="radio"
                                name={q.id}
                                checked={answers[q.id] === String(n)}
                                onChange={() => setAnswer(q.id, String(n))}
                              />
                            </label>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                )}

                {q.type === 'select' && (
                  <select
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    style={{ ...inputStyle, maxWidth: 280 }}
                  >
                    <option value="">Select</option>
                    {(q.options || []).map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                )}

                {q.type === 'checkbox' && (
                  <div style={{ display: 'grid', gap: 8 }}>
                    {(q.id === 'festival_compelling_performer' && inviteInfo?.scanned_days?.length > 0
                      ? q.options.filter(opt => {
                          const lowerOpt = opt.toLowerCase();
                          return inviteInfo.scanned_days.some(day => lowerOpt.includes(`(${day})`));
                        })
                      : q.options
                    ).map((opt) => {
                      const isTestAccount = inviteInfo?.email?.toLowerCase() === 'fshmnoelhaki2@gmail.com';
                      let displayOpt = opt;
                      let saveOpt = opt;
                      
                      if (q.id === 'festival_compelling_performer') {
                        saveOpt = opt.replace(/^\(\w+\)\s*/i, '');
                        displayOpt = isTestAccount ? opt : saveOpt;
                      }

                      return (
                        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#374151', fontSize: '0.9rem' }}>
                          <input
                            type="checkbox"
                            checked={Array.isArray(answers[q.id]) && answers[q.id].includes(saveOpt)}
                            onChange={() => toggleCheckbox(q.id, saveOpt)}
                          />
                          {displayOpt}
                        </label>
                      );
                    })}
                    {q.allowOther && (
                      <input
                        placeholder="Other (please specify)"
                        value={answers[`${q.id}_other`] || ''}
                        onChange={(e) => setAnswer(`${q.id}_other`, e.target.value)}
                        style={inputStyle}
                      />
                    )}
                  </div>
                )}

                {q.type === 'text' && (
                  <input
                    type="text"
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    style={{ ...inputStyle, fontFamily: 'inherit' }}
                  />
                )}

                {q.type === 'textarea' && (
                  <textarea
                    rows={4}
                    value={answers[q.id] || ''}
                    onChange={(e) => setAnswer(q.id, e.target.value)}
                    style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                )}
              </div>
            ))}

            {submitError && <div className="error-message" role="alert">{submitError}</div>}

            <button
              type="submit"
              disabled={submitting}
              style={{
                border: 'none',
                borderRadius: 12,
                background: submitting ? '#f59e9e' : '#dc2626',
                color: '#fff',
                display: 'block',
                width: '100%',
                minHeight: 54,
                padding: '0.95rem 1rem',
                fontSize: '1rem',
                fontWeight: 700,
                cursor: submitting ? 'not-allowed' : 'pointer',
              }}
            >
              {submitting ? 'Submitting…' : 'Submit Survey'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default PostFestivalSurveyPage;
